import React, { Suspense, useEffect, useRef, useState } from 'react';
interface LazySectionProps {
  children: React.ReactNode;
  /** Reserved height while the chunk loads, so there is no layout jump. */
  minHeight?: number;
}
function observerSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.IntersectionObserver === 'function';
}

/**
 * Mounts a below-the-fold section only when it gets within 200px of the
 * viewport, so its JS chunk is never part of the initial payload.
 *
 * Safety first: if IntersectionObserver is missing, throws, or never fires,
 * the section mounts anyway. No content can ever be permanently missing.
 */
export function LazySection({
  children,
  minHeight = 360
}: LazySectionProps) {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const [mounted, setMounted] = useState(() => !observerSupported());
  useEffect(() => {
    if (mounted) return;
    const node = nodeRef.current;
    if (!node) {
      setMounted(true);
      return;
    }
    let observer: IntersectionObserver | null = null;

    // Hard fallback: never leave a section unmounted forever.
    const failSafe = window.setTimeout(() => setMounted(true), 3000);
    try {
      observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) setMounted(true);
      }, {
        rootMargin: '200px 0px'
      });
      observer.observe(node);
    } catch {
      setMounted(true);
    }
    return () => {
      window.clearTimeout(failSafe);
      if (observer) observer.disconnect();
    };
  }, [mounted]);
  const placeholder = <div style={{
    minHeight
  }} aria-hidden="true" data-id="element-164" data-mp="2n31llcpi775f" />;
  return <div ref={nodeRef} data-id="element-165" data-mp="2n5o241udgwzs">
      {mounted ? <Suspense fallback={placeholder} data-id="element-166" data-mp="2i5teq89t2pjh">{children}</Suspense> : placeholder}
    </div>;
}